var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useState } from 'react';
import Card, { Title } from 'shared-assets/components/Card';
import InputGroup from 'shared-assets/components/Form/InputGroup';
import { useSnackbar, SnackbarProvider } from 'notistack';
import { ErrorBoundary } from 'react-error-boundary';
import { ThemeProvider, AttentionFilledIcon, CircularProgress, } from '@kareo/design-system';
import { Box, Button, Chip, Typography, } from 'shared-assets/packages/design-system/src';
import { checkPsychNotesEnabled, getDefaultPsychNoteUserSettings, } from 'shared-assets/psychotherapy-notes-ui/utils/psychNoteHelper';
import rollbar from 'shared-assets/src/errors/errors';
import { getPsychNoteUserSettings, updatePsychNoteUserSettings, } from 'shared-assets/psychotherapy-notes-ui/service/userSettings/service';
import getDesignSystemTheme from 'shared-assets/lib/utils/getDesignSystemTheme';
import { PSYCH_NOTE_OVERVIEW, SETTINGS_SAVE_ERROR, } from 'shared-assets/psychotherapy-notes-ui/constants';
import GenericLoadingErrorPage from 'shared-assets/psychotherapy-notes-ui/components/Error/GenericLoadingErrorPage';
var errorFallback = function () {
    return (React.createElement(Box, { p: 2, bgcolor: "background.paper" },
        React.createElement(GenericLoadingErrorPage, null)));
};
var PsychNoteMySettingsWrapper = function () {
    var dsTheme = getDesignSystemTheme();
    return (React.createElement(ThemeProvider, { theme: dsTheme },
        React.createElement(SnackbarProvider, { anchorOrigin: {
                vertical: 'top',
                horizontal: 'center',
            }, iconVariant: {
                error: React.createElement(AttentionFilledIcon, { fontSize: "small" }),
            } },
            React.createElement(ErrorBoundary, { FallbackComponent: errorFallback, onError: function (error) {
                    rollbar.error(error);
                } },
                React.createElement(PsychNoteMySettings, null)))));
};
var PsychNoteMySettings = function () {
    var enqueueSnackbar = useSnackbar().enqueueSnackbar;
    var _a = useState(false), isPsychotherapyNotesChecked = _a[0], setIsPsychotherapyNotesChecked = _a[1];
    var _b = useState(false), isChipGreen = _b[0], setIsChipGreen = _b[1];
    var _c = useState(true), loading = _c[0], setLoading = _c[1];
    var _d = useState(false), inError = _d[0], setInError = _d[1];
    var _e = useState(null), psychNoteUserSettings = _e[0], setPsychNoteUserSettings = _e[1];
    function retrieveUserSetting() {
        getPsychNoteUserSettings()
            .then(function (settings) {
            var psychNoteEnabled = checkPsychNotesEnabled(settings);
            setPsychNoteUserSettings(settings);
            setIsPsychotherapyNotesChecked(psychNoteEnabled);
            setIsChipGreen(psychNoteEnabled);
        })
            .catch(function (error) {
            var _a;
            if ((error === null || error === void 0 ? void 0 : error.status) === 404 || ((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.errorCode) === 404) {
                var defaultSettings = getDefaultPsychNoteUserSettings();
                setPsychNoteUserSettings(defaultSettings);
                setIsPsychotherapyNotesChecked(false);
                setIsChipGreen(false);
            }
            else {
                setInError(true);
                rollbar.error(error);
            }
        })
            .finally(function () { return setLoading(false); });
    }
    useEffect(function () {
        retrieveUserSetting();
    }, []);
    function savePsychNoteEnabled() {
        if (psychNoteUserSettings) {
            var updatedSettings = __assign(__assign({}, psychNoteUserSettings), { value: __assign(__assign({}, psychNoteUserSettings.value), { psychNotesEnabled: isPsychotherapyNotesChecked }) });
            updatePsychNoteUserSettings(updatedSettings)
                .then(function (settings) {
                var psychNoteEnabled = checkPsychNotesEnabled(settings);
                setPsychNoteUserSettings(settings);
                setIsPsychotherapyNotesChecked(psychNoteEnabled);
                setIsChipGreen(psychNoteEnabled);
            })
                .catch(function (error) {
                var _a;
                if ((error === null || error === void 0 ? void 0 : error.status) === 400 || ((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.errorCode) === 400) {
                    retrieveUserSetting();
                }
                enqueueSnackbar(SETTINGS_SAVE_ERROR, {
                    variant: 'error',
                });
                rollbar.error(error);
            });
        }
    }
    function getStatusChip() {
        return (React.createElement("span", null,
            ' ',
            isChipGreen ? (React.createElement(Chip, { type: "active", label: "Enabled" })) : (React.createElement(Chip, { type: "info", label: "Disabled" }))));
    }
    var isButtonEnabled = isPsychotherapyNotesChecked !== isChipGreen;
    return loading ? (React.createElement(Box, { display: "flex", width: "100%", height: "25vh", justifyContent: "center", alignItems: "center" },
        React.createElement(CircularProgress, { size: 30 }))) : (React.createElement(Card, { header: function () { return (React.createElement(Title, null,
            React.createElement(Typography, { variant: "h3", component: "div" },
                "Psychotherapy Notes ",
                !inError && getStatusChip()))); }, footer: !inError && (React.createElement(Box, { display: "flex" },
            React.createElement(Button, { color: "vitality", onClick: savePsychNoteEnabled, disabled: !isButtonEnabled }, "Save"))) }, inError ? (React.createElement(GenericLoadingErrorPage, null)) : (React.createElement(React.Fragment, null,
        React.createElement(Typography, { variant: "body1", component: "div" }, PSYCH_NOTE_OVERVIEW.paragraph1),
        React.createElement(Typography, { variant: "body1" }, PSYCH_NOTE_OVERVIEW.paragraph2),
        !inError && (React.createElement(InputGroup, { type: "checkbox", label: "Enable Psychotherapy Notes", onChange: function () {
                return setIsPsychotherapyNotesChecked(!isPsychotherapyNotesChecked);
            }, checked: isPsychotherapyNotesChecked, id: "enableRestrictions", "data-testid": "enable-restrictions" }))))));
};
export default PsychNoteMySettingsWrapper;
